<template>
  <div class="select" v-click-outside="hideOptions">
    <button
      :class="{
        'select--button__capitalize': capitalize
      }"
      :disabled="disabled"
      ref="select-trigger"
      type="button"
      class="select--button"
      @click="showOptions"
      @focus="$emit('focus', $event)"
    >
      <span
        v-if="
          (Array.isArray(selectedValues) && selectedValues.length === 0) ||
            !selectedValues
        "
        class="label label__placeholder"
      >
        {{ placeholder }}
      </span>

      <span v-else :title="selectedsLabel" class="label label__selected">
        <slot name="button-label" :selectedValues="selectedValues"
          >{{ capitalize ? selectedsLabel.toLowerCase() : selectedsLabel }}
        </slot>
      </span>
      <i
        class="select--button__caret"
        :class="{
          rotate: show
        }"
      ></i>
    </button>

    <!-- OPTIONS -->
    <div v-show="show" ref="select-options" class="select-options">
      <!-- SLOT DEFAULT -->
      <slot>
        <SearchInput
          v-if="search"
          v-model="searchTerm"
          :placeholder="searchPlaceholder"
          class="select-search"
        />
        <div class="select-options-list">
          <!-- INPUT PARA SELEÇÃO DE TODAS OPÇÕES -->
          <div v-if="toggleAll && multiple" class="select-options--toggle">
            <InputCheckbox
              :label="toggleAllLabel"
              :checked="allChecked"
              @change="toggleAllEvent"
            />
          </div>

          <!-- OPÇÃO -->
          <div
            v-for="option in searchResult"
            :key="`select-option-${JSON.stringify(option.value) || randomId()}`"
            :class="{
              'select-options--group': option.label
            }"
            class="select-options--item"
          >
            <!-- GRUPO -->
            <div v-if="option.label">
              <p class="select-options--group-label">
                {{ option.label }}
              </p>

              <SelectOption
                v-for="groupOpt in option.options"
                v-model="selectedValues"
                :key="
                  `select-group-option-${groupOpt.text || randomId()}-${
                    groupOpt.value
                  }`
                "
                :label="
                  capitalize ? groupOpt.text.toLowerCase() : groupOpt.text
                "
                :inputValue="groupOpt"
                :checkbox="multiple"
                :disabled="groupOpt.disabled || false"
                :class="{
                  'select-options--option__capitalize': capitalize
                }"
                class="select-options--option"
                @input="$emit('input', selectedValues)"
                @change="$emit('change', selectedValues)"
              >
                <template
                  v-for="(_, slot) in $scopedSlots"
                  v-slot:[slot]="props"
                >
                  <slot v-bind="props" :name="slot" />
                </template>
              </SelectOption>
            </div>
            <!-- OPÇÃO -->
            <div v-else class="select-options--list-item">
              <SelectOption
                v-model="selectedValues"
                :label="capitalize ? option.text.toLowerCase() : option.text"
                :inputValue="option"
                :checkbox="multiple"
                :disabled="option.disabled || false"
                :class="{
                  'select-options--option__capitalize': capitalize
                }"
                class="select-options--option"
                @click="eventHandler"
                @change="eventHandler"
              >
                <template
                  v-for="(_, slot) in $scopedSlots"
                  v-slot:[slot]="props"
                >
                  <slot v-bind="props" :name="slot" />
                </template>
              </SelectOption>
            </div>
          </div>
        </div>

        <!-- SLOT PARA ADICIONAR CONTEÚDO EXTRA ABAIXO DAS OPÇÕES -->
        <slot name="extra-content"> </slot>
      </slot>
    </div>
  </div>
</template>

<script>
/**
 * * Componente do elemento select
 */
import vClickOutside from 'v-click-outside'
import InputCheckbox from './InputCheckbox'
import SearchInput from './SearchInput'
import SelectOption from './SelectOption'
import { getRandomInt } from '@/helpers/math'
import popperMixin from '@/mixins/popperMixin'

export default {
  name: 'Select',
  mixins: [popperMixin],
  components: {
    SearchInput,
    SelectOption,
    InputCheckbox
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    /**
     * Valor do select
     */
    value: {
      type: [Array, Object, Number, String]
    },
    /**
     * Placeholder do select
     */
    placeholder: {
      type: String,
      default: 'Selecione'
    },
    /**
     * Se possuí campo de busca
     */
    search: {
      type: Boolean,
      default: false
    },
    /**
     * Placeholder do campo de busca
     */
    searchPlaceholder: {
      type: String,
      default: 'Buscar'
    },
    /**
     * Se é permitido selecionar multiplas opções
     */
    multiple: {
      type: Boolean,
      default: false
    },
    /**
     * Opções do select
     */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * Se deve apresentar uma opção para selecionar todas as opções
     */
    toggleAll: {
      type: Boolean,
      default: true
    },
    /**
     * Texto da opção de seleção de todas
     */
    toggleAllLabel: {
      type: String,
      default: 'Todas'
    },
    /** Se deve apresentar o texto de todas opções selecionadas */
    showAllOptionsSelectedText: {
      type: Boolean,
      default: true
    },
    /** Se deve capitalizar os textos das opções e opção selecionada */
    capitalize: {
      type: Boolean,
      default: false
    },
    /** Desabilita a seleção */
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedValues: null,
      searchTerm: '',
      show: false /**  flag que controla a visibilidade das opções*/,
      /**
       * Parâmetros usados para controle de listas muito grandes
       * de opções
       */
      optionsRatesLength: 0 /** Total de páginas das opções */,
      currentOptionsPage: 0 /** Página a ser adicionada */,
      currentOptions: [] /** Opções listadas atualmente */
    }
  },
  computed: {
    /**
     * Retorna o texto com o nome de todos os itens selecionados
     * @returns {String}
     * */
    selectedsLabel() {
      if (this.showAllOptionsSelectedText && this.allChecked) {
        return this.toggleAllLabel
      }

      return Array.isArray(this.selectedValues)
        ? this.selectedValues.map((item) => item.text).join(', ')
        : this.selectedValues.text
    },
    /**
     * Propriedade que filtra as opções de acordo com o termo buscado
     * @returns {Array}
     */
    searchResult() {
      if (this.searchTerm.trim() === '') return this.options

      const rgx = new RegExp(this.searchTerm, 'gmi')

      const result = []

      this.options.forEach((item) => {
        if (item.text?.search(rgx) > -1) {
          result.push(item)
        }

        // se é um grupo filtra os filhos
        if (item.label) {
          const itemCopy = JSON.parse(JSON.stringify(item))
          const group = [...item.options]
          itemCopy.options = group.filter((item) => item.text.search(rgx) > -1)

          if (itemCopy.options.length > 0) result.push(itemCopy)
        }
      })

      return result
    },
    /**
     * Retorna se todas as opções estão selecionadas
     * @returns {Boolean}
     */
    allChecked() {
      const allOptions = this.searchResult.flatMap(
        (item) => item.options || item
      )

      /**
       * Verifica se selectedValues.length é maior ou igual a allOptions.length
       * porque pode haver a situação em que o usuário seleciona todas opções e após
       * usa o filtro, fazendo que o número de opções possa ficar menor que o de seleção
       */
      return (
        allOptions.length > 0 && this.selectedValues.length >= allOptions.length
      )
    }
  },
  watch: {
    value(newVal) {
      if (this.multiple) {
        this.selectedValues = newVal || []
      } else {
        this.selectedValues = newVal
      }
    }
  },
  created() {
    this.selectedValues = this.value

    if (this.multiple) {
      this.selectedValues = this.value || []
    }
  },
  mounted() {
    if (this.multiple) {
      // verifica se todas as opções estão selecionadas
      this.selectAll = this.allChecked
    }
  },
  methods: {
    /**
     * Gera um número randomico para o key do componente
     */
    randomId() {
      return getRandomInt(0, 1000000)
    },
    hideOptions() {
      this.show = false

      /** Limpa os itens carregados */
      this.currentOptionsPage = 0
      this.currentOptions = this.multiple ? [this.options[0]] : this.options

      /** Emite um evento de blur */
      this.$emit('blur')
    },
    /**
     * Gerenciador do evento para input de multipla seleção sem grupos
     */
    eventHandler() {
      this.$emit('input', this.selectedValues)
      this.$emit('change', this.selectedValues)

      if (!this.multiple) {
        this.hideOptions()
      }
    },
    showOptions() {
      this.show = !this.show

      if (this.show) {
        this.createPopper(
          this.$refs['select-trigger'],
          this.$refs['select-options'],
          this.position
        )
      } else {
        this.destroyPopper()
      }

      /** Emite um evento de focus */
      this.$emit('focus')
    },
    /** Marca ou desmarca todas as opções apresentadas */
    toggleAllEvent(checked) {
      /** Pegar itens desabilitados */
      let disableds = this.selectedValues
      disableds = disableds.filter((el) => el.disabled)
      if (checked) {
        this.selectedValues = []

        this.searchResult.forEach((item) => {
          if (item.text) {
            this.selectedValues.push(item)
          }

          if (item.label) {
            this.selectedValues = [...this.selectedValues, ...item.options]
          }
        })
      } else {
        /** Se for multipla seleção */
        if (this.multiple) {
          this.selectedValues = []
        } else {
          this.selectedValues = null
        }
      }
      /** Remover itens desabilitados */
      const itens = this.selectedValues.filter((el) => !el.disabled)
      this.selectedValues = [...itens, ...disableds]

      this.$emit('input', this.selectedValues)
      this.$emit('change', this.selectedValues)
    },
    /** Verifica se é necessário fazer paginação */
    hasRatesLazyLoading() {
      if (this.multiple) {
        return this.options.flatMap((item) => item.options).length > 100
      }

      return this.options.length > 100
    },
    handleScroll(evt, el) {
      if (this.hasRatesLazyLoading()) {
        const distBottom = el.scrollHeight - (el.scrollTop + el.offsetHeight)

        if (distBottom < 200) {
          this.currentOptionsPage++
          this.currentOptions.push(this.options[this.currentOptionsPage])
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.select {
  position: relative;

  &-options {
    display: flex;
    flex-wrap: wrap;

    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: $dropdown-options-z-index;

    min-width: 100%;
    padding: 15px 0;
    border: 1px solid $gray-border;
    background-color: $white;

    &--toggle {
      padding-bottom: 12px;
      margin-bottom: 14px;
      border-bottom: 1px solid $gray-border;
    }

    &--group-label {
      font-size: font-size('size-medium');
      font-weight: font-weight('weight-medium');
    }

    &--item {
      width: 100%;
      white-space: nowrap;
      & + & {
        margin-top: 14px;
      }
    }

    &-list {
      max-height: 285px;
      overflow-y: auto;
      width: 100%;
      padding: 0 20px;
    }

    &--group {
      & + & {
        padding-top: 15px;
        border-top: 1px solid $gray-border;
      }

      &:first-child {
        border: none;
      }

      & + &--item & {
        border-bottom: 1px solid $gray-border;
      }

      .select-option {
        margin-top: 18px;
      }
    }
  }

  &-search {
    width: calc(100% - 22px * 2);
    margin: 0 auto 18px;
  }

  &--button {
    position: relative;
    width: 100%;
    height: $input-height;
    padding: 0;
    appearance: none;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    border: none;
    background: none;
    text-align: left;
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-normal');
    color: $gray-darkest;
    border-bottom: 1px solid $gray-darkest;

    &[disabled] {
      opacity: 0.5;
    }

    .label {
      width: calc(100% - 15px);
      max-width: 450px;
      line-height: 21px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &__placeholder {
        color: $gray-dark;
      }

      &__selected {
        color: $gray-darkest;
      }
    }

    &__caret {
      display: inline-block;
      transition: all 0.25s 0.25s;
      transform: rotate(0);

      &.rotate {
        transform: rotate(180deg);
      }

      &:after {
        content: '';
        display: inline-block;
        @include arrow(bottom, $gray-darkest, 5px);
      }
    }

    &__capitalize {
      text-transform: capitalize;
    }
  }

  ::v-deep .select-option {
    button,
    label {
      text-transform: capitalize;
    }
  }
}
</style>
